import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_BASE, useAuth } from '../lib/auth';

type ActivityRow = {
  id: string;
  submission_id: string | null;
  action: string;
  details: unknown;
  created_at: string;
  admin_email: string | null;
  admin_name: string | null;
  submitter_name: string | null;
};

export default function AdminActivity() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [activity, setActivity] = useState<ActivityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/admin/activity`, { credentials: 'include' })
      .then(async (res) => {
        if (res.status === 401) {
          navigate('/login', { replace: true });
          return null;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return (await res.json()) as { ok: boolean; activity: ActivityRow[] };
      })
      .then((data) => {
        if (!cancelled && data) setActivity(data.activity ?? []);
      })
      .catch((err) => {
        console.error('activity_fetch_failed', err);
        if (!cancelled) setError("Couldn't load activity.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  async function signOut() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <AdminPageShell active="Activity" onSignOut={signOut}>
      <div className="max-w-4xl mx-auto w-full px-4 sm:px-6 py-5 sm:py-8">
        <div className="mb-5">
          <h1 className="font-bagel text-[30px] text-burgundy leading-none">Activity</h1>
          <p className="text-[13px] text-burgundy-faint mt-1">
            The latest moves across dilemmas, replies, notes, and team changes.
          </p>
        </div>

        <div className="bg-cream-bright border border-burgundy/[0.12] rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-6 text-burgundy-faint text-sm">Loading...</div>
          ) : error ? (
            <div className="p-6 text-danger text-sm font-semibold">{error}</div>
          ) : activity.length === 0 ? (
            <div className="p-6 text-burgundy-faint text-sm">Nothing has happened yet.</div>
          ) : (
            activity.map((item) => (
              <div
                key={item.id}
                className="px-4 py-3.5 border-b last:border-b-0 border-burgundy/[0.10]"
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                  <div className="text-[14px] text-burgundy font-semibold">
                    {item.admin_name || item.admin_email || 'System'} {labelAction(item)}
                  </div>
                  <div className="text-[12px] text-burgundy-faint">
                    {formatWhen(item.created_at)}
                  </div>
                </div>
                {item.submitter_name && (
                  <div className="text-[12px] text-burgundy-faint mt-1">
                    Dilemma: {item.submitter_name}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </AdminPageShell>
  );
}

export function AdminPageShell({
  active,
  onSignOut,
  children,
}: {
  active: 'Inbox' | 'Forms' | 'Activity' | 'Settings';
  onSignOut: () => void;
  children: React.ReactNode;
}) {
  const nav = [
    { label: 'Inbox', href: '/admin' },
    { label: 'Forms', href: '/admin/forms' },
    { label: 'Activity', href: '/admin/activity' },
    { label: 'Settings', href: '/admin/settings' },
  ];
  return (
    <div className="min-h-[100dvh] bg-cream-soft text-burgundy font-hanken">
      <header className="bg-forest text-cream-bright px-3 sm:px-6 py-3 flex items-center gap-3">
        <Link to="/admin" className="font-rye text-[14px] sm:text-[18px] whitespace-nowrap">
          I SAID WHAT I SAID
        </Link>
        <nav className="ml-auto flex items-center gap-1">
          {nav.map((item) => (
            <Link
              key={item.label}
              to={item.href}
              className={
                'px-3 py-2 rounded-lg text-[13px] font-bold ' +
                (active === item.label
                  ? 'bg-forest-soft text-cream-bright'
                  : 'text-cream-bright/70 hover:text-cream-bright')
              }
            >
              {item.label}
            </Link>
          ))}
          <button
            type="button"
            onClick={onSignOut}
            className="ml-1 px-3 py-2 rounded-lg text-[13px] font-bold bg-pink text-cream-bright"
          >
            Sign out
          </button>
        </nav>
      </header>
      {children}
    </div>
  );
}

function labelAction(item: ActivityRow): string {
  const action = item.action.replace(/_/g, ' ');
  if (item.action === 'status_changed') {
    const d = item.details as { to?: string } | null;
    return `moved a dilemma to ${d?.to ?? 'a new status'}`;
  }
  return action;
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
